import type { IssueSummary } from '@cortex/domain-ac-mens-hub-api';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { formatShortDate, ISSUE_PRIORITY_LABEL_KEYS } from '../../lib/issue-mgmt-format.js';
import { nextActionLabel, waitingLabel } from '../../lib/issue-mgmt-next-action.js';
import { IssueStatusBadge } from './IssueStatusBadge.js';
import { IssueText } from './IssueText.js';

/**
 * 課題の詳細の住所。
 * @graph-connects none
 */
function issuePath(issueId: string): string {
  return `/issue-mgmt/issues/${issueId}`;
}

/** 見出しのセル。 @graph-connects none */
const TH_CLASS = 'px-3 py-2.5 text-left text-xs font-semibold text-[var(--color-text-muted)]';

/** 本文のセル。 @graph-connects none */
const TD_CLASS = 'px-3 py-3 align-top text-sm';

/**
 * 一覧の「次にやること」の列。次にやることがあれば文言と受け持つ人（期限切れなら赤）、無ければ待ちの様子を薄く出す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の一覧の次にやることの列に、判定表の文言と受け持つ人を出し、期限切れを赤にする
 * @graph-connects nextActionLabel [calls] 次にやることの文言
 * @graph-connects waitingLabel [calls] 待ちの様子
 */
function NextCell({ issue, now }: { issue: IssueSummary; now: Date }) {
  const { t } = useTranslation();
  if (issue.next === null) {
    return (
      <span className="text-[var(--color-text-muted)]">
        {issue.waiting === null
          ? t('issueMgmt.common.dash')
          : waitingLabel(issue.waiting, t, (date) => formatShortDate(date, now))}
      </span>
    );
  }
  return (
    <span
      className={
        issue.next.overdue ? 'text-[var(--color-error)]' : 'text-[var(--color-text-primary)]'
      }
    >
      {nextActionLabel(issue.next, t)}
      {issue.next.who !== null && (
        <span className="ml-1 text-xs text-[var(--color-text-muted)]">
          {issue.next.who.displayName}
        </span>
      )}
    </span>
  );
}

/**
 * 課題の一覧の表。列は 課題（番号を小さく＋タイトル）・状態・優先度・担当・声・次にやることの6つで、
 * CS向け表示のときは最後に「今の案内」を足す。行を押すと詳細が開く（Cmd/Ctrl/Shift を押しながらなら新しいタブだけで開く）。検索で声の本文に当たった課題は「声: …」、
 * 統合済みの課題の番号に当たったら「K-xxx はこの課題に統合済み」を添える。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の一覧を6列（CS向け表示では今の案内を足して7列）の表で見せ、行を押すと詳細を開く
 * @graph-connects IssueStatusBadge [calls] 状態の札
 * @graph-connects IssueText [calls] 検索語の色と伏せ字の札
 * @graph-connects NextCell [calls] 次にやることの列
 */
export function IssueListTable({
  issues,
  query,
  showGuidance,
  onOpen,
  now,
}: {
  issues: readonly IssueSummary[];
  query: string;
  showGuidance: boolean;
  onOpen: (issueId: string) => void;
  now: Date;
}) {
  const { t } = useTranslation();
  const dash = t('issueMgmt.common.dash');
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <table className="w-full min-w-[760px]">
        <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <tr>
            <th scope="col" className={TH_CLASS}>
              {t('issueMgmt.list.colIssue')}
            </th>
            <th scope="col" className={TH_CLASS}>
              {t('issueMgmt.list.colStatus')}
            </th>
            <th scope="col" className={TH_CLASS}>
              {t('issueMgmt.list.colPriority')}
            </th>
            <th scope="col" className={TH_CLASS}>
              {t('issueMgmt.list.colAssignee')}
            </th>
            <th scope="col" className={TH_CLASS}>
              {t('issueMgmt.list.colVoices')}
            </th>
            <th scope="col" className={TH_CLASS}>
              {t('issueMgmt.list.colNext')}
            </th>
            {showGuidance && (
              <th scope="col" className={TH_CLASS}>
                {t('issueMgmt.list.colGuidance')}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {issues.map((issue) => (
            <tr
              key={issue.id}
              onClick={(e) => {
                // 修飾キーつきは新しいタブだけで開き、今の画面は動かさない
                if (e.metaKey || e.ctrlKey || e.shiftKey) {
                  window.open(issuePath(issue.id), '_blank', 'noopener');
                  return;
                }
                onOpen(issue.id);
              }}
              className="cursor-pointer hover:bg-[var(--color-bg-hover)]"
            >
              <td className={TD_CLASS}>
                <Link
                  to="/issue-mgmt/issues/$issueId"
                  params={{ issueId: issue.id }}
                  className="font-semibold text-[var(--color-text-primary)]"
                  onClick={(e) => {
                    // 遷移はリンクだけが行う（行の onClick と二重に動かさない）。修飾キーつきはブラウザが新しいタブで開く
                    e.stopPropagation();
                  }}
                >
                  <span className="mr-2 text-xs font-normal text-[var(--color-text-muted)]">
                    <IssueText text={issue.id} query={query} />
                  </span>
                  <IssueText text={issue.title} query={query} />
                </Link>
                {issue.matchedVoice !== null && (
                  <p className="mt-1 truncate text-xs text-[var(--color-text-secondary)]">
                    {t('issueMgmt.list.matchedVoice')}
                    <IssueText text={issue.matchedVoice.text} query={query} />
                  </p>
                )}
                {issue.mergedFromMatched.map((id) => (
                  <p key={id} className="mt-1 text-xs text-[var(--color-text-secondary)]">
                    {t('issueMgmt.list.mergedMatched', { id })}
                  </p>
                ))}
              </td>
              <td className={TD_CLASS}>
                <IssueStatusBadge status={issue.displayStatus} />
              </td>
              <td className={TD_CLASS}>
                {issue.priority === null ? dash : t(ISSUE_PRIORITY_LABEL_KEYS[issue.priority])}
              </td>
              <td className={TD_CLASS}>{issue.assignee?.displayName ?? dash}</td>
              <td className={`${TD_CLASS} tabular-nums`}>{issue.voiceCount}</td>
              <td className={TD_CLASS}>
                <NextCell issue={issue} now={now} />
              </td>
              {showGuidance && (
                <td className={`${TD_CLASS} max-w-xs text-[var(--color-text-secondary)]`}>
                  {issue.guidance === null ? (
                    <span className="text-[var(--color-text-muted)]">
                      {t('issueMgmt.list.guidanceEmpty')}
                    </span>
                  ) : (
                    <IssueText text={issue.guidance} query={query} />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
